import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Moment from "react-moment";
import SlideOutSidebar from "../../../ui-components/slide-out-sidebar/slideOutSidebar";
import Amount from "../../../ui-components/molecules/amount/amount";
import Loading from "../../../ui-components/loading/loading";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import PdfActionSplitButton from "../../../ui-components/pdf-action-split-button/pdfActionSplitButton";
import Tag from "../../../ui-components/molecules/tag/tag";
import { TagColor } from "../../../app/data/common/tagColor";
import PaymentService from "../../../app/data/payment/paymentService";
import { PaymentSummary } from "../../../app/data/payment/PaymentSession";
import {
  deliverPdf,
  getPdfDownloadErrorMessage,
} from "../../../app/data/shipping-docs/downloadPdf";
import { PdfDocumentAction } from "../../../app/data/shipping-docs/pdfDocumentActionStorage";
import { Routes } from "../../../app/route/RoutesConfig";
import { userSelector } from "../../../slices/user/userSlice";
import "./shipmentPaymentHistorySidebar.scss";

const paymentService = PaymentService.getInstance();

const statusColor = (status: string): TagColor => {
  switch (status) {
    case "COMPLETED":
      return TagColor.GREEN;
    case "PENDING":
    case "CREATED":
      return TagColor.BLUE;
    case "FAILED":
      return TagColor.RED;
    default:
      return TagColor.GREY;
  }
};

const methodLabel = (row: PaymentSummary): string => {
  if (row.paymentMethodLabel) return row.paymentMethodLabel;
  if (row.paymentMethod === "CC") return "Credit Card";
  if (row.paymentMethod === "CK" || row.paymentMethod === "BANK") return "eCheck";
  return row.paymentMethod || "—";
};

interface ShipmentPaymentHistorySidebarProps {
  show: boolean;
  onClose: () => void;
  invoiceNumber: number;
  accountNumber?: number;
  canOpenPaymentBrowser?: boolean;
}

const ShipmentPaymentHistorySidebar: React.FC<ShipmentPaymentHistorySidebarProps> = ({
  show,
  onClose,
  invoiceNumber,
  accountNumber,
  canOpenPaymentBrowser,
}) => {
  const [rows, setRows] = useState<PaymentSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfBusyId, setPdfBusyId] = useState<string | null>(null);
  const userState = useSelector(userSelector);
  const adminPathPrefix = userState.activeTerminal?.id
    ? `/${userState.activeTerminal.id}`
    : "";

  useEffect(() => {
    if (!show || !invoiceNumber) {
      return;
    }
    setLoading(true);
    setError(null);
    const request = canOpenPaymentBrowser
      ? paymentService.adminSearchPayments({
          invoiceNumber,
          page: 0,
          size: 50,
        })
      : paymentService.listPayments({
          accountNumber,
          invoiceNumber,
          page: 0,
          size: 50,
        });
    request.then((res) => {
      if (res.ok() && res.data) {
        setRows(res.data.content || []);
      } else {
        setError(
          typeof res.getError?.() === "string"
            ? (res.getError() as string)
            : "Failed to load payment history"
        );
        setRows([]);
      }
      setLoading(false);
    });
  }, [show, invoiceNumber, accountNumber, canOpenPaymentBrowser]);

  const onPdfAction = async (paymentId: string, action: PdfDocumentAction) => {
    setPdfBusyId(paymentId);
    setError(null);
    try {
      const res = await paymentService.getPaymentReceiptPdf(paymentId);
      if (!res.ok() || !res.data) {
        throw new Error(
          typeof res.getError?.() === "string"
            ? (res.getError() as string)
            : "Failed to download receipt PDF"
        );
      }
      deliverPdf(res.data, `payment-receipt-${paymentId}.pdf`, action);
    } catch (e) {
      setError(getPdfDownloadErrorMessage(e, "Failed to download receipt PDF"));
    } finally {
      setPdfBusyId(null);
    }
  };

  return (
    <SlideOutSidebar
      show={show}
      onClose={onClose}
      header="Payment history"
      size="small-500"
    >
      <div className="xgs-shipment-payment-history">
        <Loading isLoading={loading} />
        {error && <XGSErrorMessage>{error}</XGSErrorMessage>}
        {!loading && rows.length === 0 && !error && (
          <p className="xgs-shipment-payment-history__empty">
            No payments found for this invoice.
          </p>
        )}
        <ul className="xgs-shipment-payment-history__list">
          {rows.map((row) => (
            <li key={row.paymentId} className="xgs-shipment-payment-history__item">
              <div className="xgs-shipment-payment-history__head">
                <div className="xgs-shipment-payment-history__identity">
                  <div className="xgs-shipment-payment-history__method">{methodLabel(row)}</div>
                  <div className="xgs-shipment-payment-history__date">
                    {row.dateCreated ? (
                      <Moment format="MMM D, YYYY · h:mm A">{row.dateCreated}</Moment>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
                <Tag mods={{ color: statusColor(row.status) }}>{row.status}</Tag>
              </div>
              <div className="xgs-shipment-payment-history__amount">
                {row.amountCents != null ? (
                  <Amount amount={row.amountCents / 100} />
                ) : (
                  "—"
                )}
                {row.surchargeAmountCents != null && row.surchargeAmountCents > 0 && (
                  <span className="xgs-shipment-payment-history__surcharge">
                    includes surcharge of <Amount amount={row.surchargeAmountCents / 100} />
                  </span>
                )}
              </div>
              <dl className="xgs-shipment-payment-history__meta">
                <div>
                  <dt>Paid by</dt>
                  <dd>{row.userCreated || "—"}</dd>
                </div>
                <div>
                  <dt>Invoices</dt>
                  <dd>{(row.invoiceNumbers || []).join(", ") || "—"}</dd>
                </div>
              </dl>
              {row.status === "COMPLETED" && (
                <div className="xgs-shipment-payment-history__actions">
                  <PdfActionSplitButton
                    onAction={(action) => onPdfAction(row.paymentId, action)}
                    spinner={pdfBusyId === row.paymentId}
                  >
                    Receipt
                  </PdfActionSplitButton>
                </div>
              )}
            </li>
          ))}
        </ul>
        {canOpenPaymentBrowser && (
          <div className="xgs-shipment-payment-history__admin-link">
            <Link to={`${adminPathPrefix}${Routes.paymentAdmin}?invoiceNumber=${invoiceNumber}`}>
              View more detail in Payment Browser
            </Link>
          </div>
        )}
      </div>
    </SlideOutSidebar>
  );
};

export default ShipmentPaymentHistorySidebar;
